import { EventEmitter } from './EventEmitter.js';
import { ActionDispatcher } from './actions/dispatcher.js';
import { cloneState, StateHistory } from './GameState.js';
/**
 * Headless 게임 코어 구현
 * GUI 없이 순수 게임 로직만 처리
 */
export class GameCore {
    constructor(config) {
        this.dispatcher = new ActionDispatcher();
        this.emitter = new EventEmitter();
        this.systems = [];
        this.createInitialState = config.createInitialState;
        this.state = config.createInitialState();
        this.debug = config.debug ?? false;
        this.history = new StateHistory(config.historySize ?? 100);
        if (this.debug) {
            this.history.push(this.state, 'INIT');
        }
    }
    /**
     * 현재 상태 조회 (읽기 전용)
     */
    getState() {
        return this.state;
    }
    /**
     * 액션 핸들러 등록
     */
    registerAction(type, handler) {
        this.dispatcher.register(type, handler);
    }
    /**
     * 틱 시스템 등록
     */
    registerSystem(system) {
        this.systems.push(system);
    }
    /**
     * 액션 디스패치
     */
    dispatch(action) {
        const previousState = this.debug ? cloneState(this.state) : undefined;
        const { newState, error } = this.dispatcher.dispatch(this.state, action);
        if (error) {
            const err = new Error(error);
            this.emitter.emit('error', err, action);
            return {
                success: false,
                error,
                stateChanged: false,
                previousState,
                newState: undefined
            };
        }
        if (newState === null) {
            return {
                success: true,
                stateChanged: false,
                previousState,
                newState: this.debug ? cloneState(this.state) : undefined
            };
        }
        this.state = newState;
        if (this.debug) {
            this.history.push(this.state, action.type);
        }
        this.emitter.emit('stateChanged', this.state, action);
        return {
            success: true,
            stateChanged: true,
            previousState,
            newState: this.debug ? cloneState(this.state) : undefined
        };
    }
    /**
     * 게임 틱 (매 프레임 호출)
     */
    tick(deltaTime) {
        // 각 시스템에서 액션을 수집
        const pendingActions = [];
        const queueAction = (action) => pendingActions.push(action);
        // 모든 시스템 업데이트
        for (const system of this.systems) {
            try {
                this.state = system.update(this.state, deltaTime, queueAction);
            }
            catch (error) {
                this.emitter.emit('error', error);
            }
        }
        // 수집된 액션 디스패치
        for (const action of pendingActions) {
            this.dispatch(action);
        }
        this.emitter.emit('tick', deltaTime, this.state);
    }
    /**
     * 이벤트 리스너 등록
     */
    on(event, callback) {
        return this.emitter.on(event, callback);
    }
    /**
     * 시간 빨리감기 (테스트용)
     */
    fastForward(seconds, tickRate = 60) {
        const deltaTime = 1 / tickRate;
        const totalTicks = Math.floor(seconds * tickRate);
        for (let i = 0; i < totalTicks; i++) {
            this.tick(deltaTime);
        }
    }
    /**
     * 상태 리셋
     */
    reset(partialState) {
        this.state = {
            ...this.createInitialState(),
            ...partialState
        };
        if (this.debug) {
            this.history.clear();
            this.history.push(this.state, 'RESET');
        }
        this.emitter.emit('reset', this.state);
    }
    /**
     * 상태 히스토리 (디버그 모드에서만)
     */
    getHistory() {
        return this.debug ? this.history : null;
    }
    /**
     * 등록된 액션 타입 목록
     */
    getRegisteredActions() {
        return this.dispatcher.getRegisteredTypes();
    }
    /**
     * 등록된 시스템 목록
     */
    getRegisteredSystems() {
        return this.systems.map(s => s.name);
    }
}
//# sourceMappingURL=GameCore.js.map